import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Info as InfoIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

import { bookingOrigin } from '../utils/siteUrls';
const LineSettings: React.FC = () => {
  const { shop } = useAuth();
  const [channelAccessToken, setChannelAccessToken] = useState('');
  const [channelSecret, setChannelSecret] = useState('');
  const [liffId, setLiffId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [features, setFeatures] = useState({
    reservationConfirmation: true,
    reminderBeforeDay: true,
    cancelNotification: true,
  });
  const [adminLineUserIds, setAdminLineUserIds] = useState<string[]>([]);
  const [newAdminLineUserId, setNewAdminLineUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (shop?.lineMessaging) {
      setChannelAccessToken(shop.lineMessaging.channelAccessToken);
      setChannelSecret(shop.lineMessaging.channelSecret);
      setEnabled(shop.lineMessaging.enabled);
      setFeatures(shop.lineMessaging.features);
      setAdminLineUserIds(shop.lineMessaging.adminLineUserIds || []);
    }
    // LIFF IDを読み込む
    if (shop?.liffId) {
      setLiffId(shop.liffId);
    }
  }, [shop]);

  const handleSave = async () => {
    if (!shop) return;

    if (!channelAccessToken || !channelSecret) {
      setError('Channel Access TokenとChannel Secretは必須です');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const shopRef = doc(db, 'shops', shop.id);
      const updateData: Record<string, any> = {
        lineMessaging: {
          channelAccessToken,
          channelSecret,
          enabled,
          features,
          adminLineUserIds,
        },
        updatedAt: new Date(),
      };

      // LIFF IDがある場合のみ更新
      if (liffId) {
        updateData.liffId = liffId;
      }

      await updateDoc(shopRef, updateData);

      setSuccess('LINE設定を保存しました');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('LINE設定の保存エラー:', err);
      setError('LINE設定の保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdminLineUserId = () => {
    if (!newAdminLineUserId.trim()) {
      setError('LINE User IDを入力してください');
      return;
    }
    if (adminLineUserIds.includes(newAdminLineUserId.trim())) {
      setError('このLINE User IDは既に登録されています');
      return;
    }
    setAdminLineUserIds([...adminLineUserIds, newAdminLineUserId.trim()]);
    setNewAdminLineUserId('');
    setError(null);
  };

  const handleRemoveAdminLineUserId = (userId: string) => {
    setAdminLineUserIds(adminLineUserIds.filter(id => id !== userId));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ChatIcon sx={{ fontSize: 32, mr: 2 }} />
        <Typography variant="h4">LINE Messaging API設定</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* セットアップガイド */}
      <Card sx={{ mb: 3, bgcolor: '#e3f2fd' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <InfoIcon color="info" sx={{ mr: 2, mt: 0.5 }} />
            <Box sx={{ width: '100%' }}>
              <Typography variant="h6" gutterBottom>
                LINE Messaging APIのセットアップ手順
              </Typography>

              {/* 重要な説明 */}
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Webhookの登録は不要です！</strong><br />
                  このシステムはPush Message APIを使用して通知を送信するため、LINE側でWebhook URLを設定する必要はありません。
                </Typography>
              </Alert>

              {/* 用語説明 */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  各項目の説明
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Channel ID</strong>: チャネルを識別する数字のID（例: 1234567890）- <em>※この画面では不要</em>
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Channel Secret</strong>: チャネルの秘密鍵（32文字の英数字）- <em>署名検証に使用</em>
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <strong>Channel Access Token</strong>: APIを呼び出すためのトークン（長い文字列）- <em>メッセージ送信に必要</em>
                    </Typography>
                  </li>
                </Box>
              </Paper>

              {/* 注意事項 */}
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>注意：</strong>「LINE Official Account Manager」と「LINE Developers Console」は別のサイトです。<br />
                  Channel Secretは両方で確認できますが、<strong>Channel Access Tokenは「LINE Developers Console」でのみ発行</strong>できます。
                </Typography>
              </Alert>

              {/* セットアップ手順 */}
              <Typography variant="subtitle2" gutterBottom color="primary">
                セットアップ手順
              </Typography>
              <Box sx={{ pl: 1 }}>
                <Typography component="div" variant="body2" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ minWidth: 24, fontWeight: 'bold' }}>1.</Box>
                  <Box>
                    <strong>Channel Secret を取得</strong><br />
                    <Link href="https://manager.line.biz/" target="_blank" rel="noopener">
                      LINE Official Account Manager
                    </Link> →「設定」→「Messaging API」で「Channel secret」をコピー<br />
                    <Typography variant="caption" color="text.secondary">
                      → 下の「Channel Secret」欄に貼り付け
                    </Typography>
                  </Box>
                </Typography>
                <Typography component="div" variant="body2" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ minWidth: 24, fontWeight: 'bold' }}>2.</Box>
                  <Box>
                    <strong>Channel Access Token を発行</strong><br />
                    同じ画面の下部にある「LINE Developersコンソール」リンクをクリック<br />
                    または <Link href="https://developers.line.biz/console/" target="_blank" rel="noopener">
                      LINE Developers Console
                    </Link> に直接アクセス
                  </Box>
                </Typography>
                <Typography component="div" variant="body2" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ minWidth: 24, fontWeight: 'bold' }}>3.</Box>
                  <Box>
                    LINE Developers Consoleで該当チャネルを選択<br />
                    →「<strong>Messaging API設定</strong>」タブを開く<br />
                    →「チャネルアクセストークン（長期）」の「<strong>発行</strong>」ボタンをクリック<br />
                    <Typography variant="caption" color="text.secondary">
                      → 発行されたトークンを下の「Channel Access Token」欄に貼り付け
                    </Typography>
                  </Box>
                </Typography>
                {/* 1〜3 と同じく component="div" を付ける。
                    既定の <p> の中に <div> は入れられず、
                    「hydration error」の警告が出る。 */}
                <Typography component="div" variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ minWidth: 24, fontWeight: 'bold' }}>4.</Box>
                  <Box>このページの「設定を保存」ボタンをクリック</Box>
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* LINE ログイン設定（LIFF） */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          LINE ログイン設定（LIFF）
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* LIFF ID設定状況 */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            現在の設定状況
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              LINE ログイン:
            </Typography>
            {liffId ? (
              <Chip label="設定済み" color="success" size="small" />
            ) : (
              <Chip label="未設定" color="default" size="small" />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            ※ LIFFを設定すると、お客様がLINEでログインして予約できるようになります
          </Typography>
        </Box>

        {/* LIFFセットアップ手順 */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            LIFFアプリのセットアップ手順
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2 }}>
            <li>
              <Typography variant="body2">
                <Link href="https://developers.line.biz/console/" target="_blank" rel="noopener">
                  LINE Developers Console
                </Link>にアクセス
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>LINE Loginチャネル</strong>を作成（Messaging APIチャネルとは別）
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                「LIFF」タブを開き、「追加」をクリック
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                以下の設定でLIFFアプリを作成:
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                <li><Typography variant="body2">サイズ: <strong>Full</strong></Typography></li>
                <li><Typography variant="body2">エンドポイントURL: <strong>${bookingOrigin()}/{shop?.shopCode || '{店舗コード}'}</strong></Typography></li>
                <li><Typography variant="body2">Scope: <strong>openid</strong>、<strong>profile</strong> をチェック</Typography></li>
                <li><Typography variant="body2">友だち追加オプション: <strong>On (Aggressive)</strong>（推奨）</Typography></li>
              </Box>
            </li>
            <li>
              <Typography variant="body2">
                LINE Loginチャネルの設定 →「リンクされたLINE公式アカウント」で<strong>Messaging APIチャネルとリンク</strong>
              </Typography>
              <Typography variant="caption" color="error">
                ※ これを設定しないと、LINE通知が送信できません
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                作成されたLIFF IDを下の欄に入力して保存
              </Typography>
            </li>
          </Box>
        </Alert>

        <TextField
          fullWidth
          label="LIFF ID"
          value={liffId}
          onChange={(e) => setLiffId(e.target.value)}
          placeholder="例: 1234567890-abcdefgh"
          helperText="LINE Developers ConsoleでLIFFアプリを作成し、LIFF IDを入力してください"
          sx={{ mb: 2 }}
        />
      </Paper>

      {/* API設定 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          API認証情報（Messaging API）
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* 設定状況の表示 */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            現在の設定状況
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Messaging APIチャネル:
            </Typography>
            {channelAccessToken && channelSecret ? (
              <Chip label="設定済み" color="success" size="small" />
            ) : (
              <Chip label="未設定" color="default" size="small" />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            ※ Messaging APIチャネルを設定すると、予約確認やキャンセルなどのLINE通知が送信できるようになります
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Channel Access Token"
          value={channelAccessToken}
          onChange={(e) => setChannelAccessToken(e.target.value)}
          placeholder="例: abc123def456..."
          helperText="LINE Developers ConsoleのMessaging API設定から取得できます"
          sx={{ mb: 3 }}
          type="password"
        />

        <TextField
          fullWidth
          label="Channel Secret"
          value={channelSecret}
          onChange={(e) => setChannelSecret(e.target.value)}
          placeholder="例: xyz789..."
          helperText="LINE Developers Consoleのチャネル基本設定から取得できます"
          sx={{ mb: 3 }}
          type="password"
        />

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              color="primary"
            />
          }
          label="LINE通知を有効にする"
        />
      </Paper>

      {/* 管理者LINE User ID設定 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          店舗管理者への通知設定
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            店舗管理者のLINE User IDを登録すると、新規予約が入った際に管理者にもLINE通知が送信されます。
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>LINE User IDの確認方法:</strong> LINE公式アカウントに管理者が友だち登録し、メッセージを送信すると、
            Webhook URLから確認できます。詳しくは<Link href="https://developers.line.biz/ja/docs/messaging-api/getting-user-ids/" target="_blank" rel="noopener">
              ドキュメント
            </Link>をご覧ください。
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="管理者LINE User ID"
            value={newAdminLineUserId}
            onChange={(e) => setNewAdminLineUserId(e.target.value)}
            placeholder="例: U1234567890abcdef..."
            helperText="管理者のLINE User IDを入力してください"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddAdminLineUserId();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddAdminLineUserId}
            sx={{ minWidth: 100 }}
          >
            追加
          </Button>
        </Box>

        {adminLineUserIds.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              登録済み管理者 ({adminLineUserIds.length}人)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {adminLineUserIds.map((userId) => (
                <Chip
                  key={userId}
                  label={userId}
                  onDelete={() => handleRemoveAdminLineUserId(userId)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* 通知機能設定 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          顧客への通知機能
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={features.reservationConfirmation}
                onChange={(e) => setFeatures({
                  ...features,
                  reservationConfirmation: e.target.checked
                })}
                disabled={!enabled}
              />
            }
            label={
              <Box>
                <Typography variant="body1">予約確認通知</Typography>
                <Typography variant="caption" color="text.secondary">
                  予約が完了した直後に確認メッセージを送信します
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={features.reminderBeforeDay}
                onChange={(e) => setFeatures({
                  ...features,
                  reminderBeforeDay: e.target.checked
                })}
                disabled={!enabled}
              />
            }
            label={
              <Box>
                <Typography variant="body1">予約リマインダー通知</Typography>
                <Typography variant="caption" color="text.secondary">
                  予約日の前日にリマインダーメッセージを送信します
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={features.cancelNotification}
                onChange={(e) => setFeatures({
                  ...features,
                  cancelNotification: e.target.checked
                })}
                disabled={!enabled}
              />
            }
            label={
              <Box>
                <Typography variant="body1">キャンセル通知</Typography>
                <Typography variant="caption" color="text.secondary">
                  予約がキャンセルされた際に通知メッセージを送信します
                </Typography>
              </Box>
            }
          />
        </Box>
      </Paper>

      {/* 保存ボタン */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          {loading ? '保存中...' : '設定を保存'}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>料金について:</strong> LINE公式アカウントのプランによってメッセージ送信数に制限があります。
          詳しくは<Link href="https://www.linebiz.com/jp/service/line-official-account/" target="_blank" rel="noopener" sx={{ ml: 1 }}>
            LINE公式アカウント
          </Link>のプランをご確認ください。
        </Typography>
      </Alert>
    </Container>
  );
};

export default LineSettings;
